/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useCallback } from 'react';

import { InviteAgentParams, UseInviteAgent } from './useInviteAgent.decl';

import { InviteAgentMutation, InviteAgentMutationVariables } from '@generated/InviteAgentMutation';
import { INVITE_AGENT_MUTATION } from '@graphql/mutations/InviteAgentQuery';
import { SEARCH_TOTAL_QUERY } from '@graphql/queries/SearchTotalQuery';
import { useFromOnboarding } from '@hooks/useFromOnboarding';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { usePermissions } from '@hooks/usePermissions';
import { useTracker } from '@hooks/useTracker/useTracker';

/**
 * Hook to create a user given a handler for submit success behaviour.
 * @param useCreateAgentParams - destructured parameter
 * @returns an object holding a function responsible for creating a user
 */
export function useInviteAgent({ onCompleted, funnelId }: UseInviteAgent) {
  const { track } = useTracker();
  const isFromOnboarding = useFromOnboarding();

  const [mutate] = useGraphMutationWithFeedback<InviteAgentMutation, InviteAgentMutationVariables>(
    INVITE_AGENT_MUTATION,
    {
      type: 'form',
    }
  );

  const { roles: userRoles } = usePermissions();

  const inviteAgent = useCallback(
    async (values: InviteAgentParams) => {
      const { data, error } = await mutate({
        variables: {
          input: {
            ...values,
            roles: values.roles.map((item) => {
              const { __typename, ...rest } = item;
              return rest;
            }),
          },
        },
        refetchQueries: [
          {
            // refetch total query after creation
            query: SEARCH_TOTAL_QUERY,
          },
        ],
      });

      const userPermissionSet = userRoles.map((item) => item.name).join(';');
      const targetUserPermissionSet = values.roles.map((item) => item.name).join(';');

      if (error) {
        const errorKey = Object.keys(error)[0];
        const firstError = error[errorKey as keyof typeof error];
        track({
          event: 'user_creation_funnel_rejected',
          payload: {
            error: firstError,
            user_permission_set: userPermissionSet,
            target_user_permission_set: targetUserPermissionSet,
            is_part_of_onboarding: isFromOnboarding,
            funnel_id: funnelId,
          },
        });

        return error;
      }

      track({
        event: 'user_creation_funnel_completed',
        payload: {
          user_permission_set: userPermissionSet,
          target_user_permission_set: targetUserPermissionSet,
          target_user_id: data.data?.inviteUser?.ID,
          is_part_of_onboarding: isFromOnboarding,
          funnel_id: funnelId,
        },
      });

      onCompleted(data.data?.inviteUser?.ID);

      return undefined;
    },
    [funnelId, isFromOnboarding, mutate, onCompleted, track, userRoles]
  );

  return { inviteAgent };
}
